import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

// Generate PDF Report
export function generateAppraisalPDF(appraisal, user) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const hideManagerSection = appraisal?.visibleToSupervisor === false || user?.role === 'supervisor';
  
  // Header
  doc.setFontSize(20);
  doc.setTextColor(13, 148, 136); // Teal color
  doc.text('Performance Appraisal Report', pageWidth / 2, 20, { align: 'center' });
  
  // Employee Info
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text(`Employee: ${user?.profile?.firstName || ''} ${user?.profile?.lastName || ''}`, 20, 40);
  doc.text(`Role: ${user?.role || 'N/A'}`, 20, 48);
  doc.text(`Position: ${user?.profile?.position || 'N/A'}`, 20, 56);
  doc.text(`Department: ${user?.profile?.department || 'N/A'}`, 20, 64);
  doc.text(`Appraisal: ${appraisal?.title || 'Performance Review'}`, 20, 72);
  doc.text(`Status: ${appraisal?.status || 'N/A'}`, 20, 80);
  doc.text(`Employee Submitted: ${appraisal?.employeeSubmittedAt ? new Date(appraisal.employeeSubmittedAt).toLocaleString() : 'N/A'}`, 20, 88);
  if (!hideManagerSection || appraisal?.managerSubmittedAt || appraisal?.finalizedAt) {
    const managerFinalizedText = appraisal?.managerSubmittedAt || appraisal?.finalizedAt
      ? new Date(appraisal.managerSubmittedAt || appraisal.finalizedAt).toLocaleString()
      : 'Not required';
    doc.text(`Manager Finalized: ${managerFinalizedText}`, 20, 96);
  }
  doc.text(`Exported: ${new Date().toLocaleDateString()}`, 20, 104);
  
  // Line separator
  doc.setDrawColor(200, 200, 200);
  doc.line(20, 104, pageWidth - 20, 104);
  
  // Performance Areas
  doc.setFontSize(14);
  doc.setTextColor(13, 148, 136);
  doc.text('Performance Areas', 20, 116);
  
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  
  let yPos = 128;
  appraisal?.performanceAreas?.forEach((area, index) => {
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFontSize(11);
    doc.text(`${index + 1}. ${area.competencyName}`, 20, yPos);
    doc.setFontSize(9);
    doc.text(`Weight: ${area.weight}%`, 30, yPos + 6);
    doc.text(`Self Score: ${area.employeeScore ?? 'N/A'}`, 80, yPos + 6);
    if (!hideManagerSection) {
      doc.text(`Manager Score: ${area.managerScore ?? 'N/A'}`, 130, yPos + 6);
      if (area.managerComment) {
        const commentLines = doc.splitTextToSize(`Comment: ${area.managerComment}`, pageWidth - 50);
        doc.text(commentLines, 30, yPos + 12);
        yPos += 6 * commentLines.length;
      }
    }
    yPos += 18;
  });
  
  // Final Scores
  if (yPos > 250) {
    doc.addPage();
    yPos = 20;
  }
  
  doc.setFontSize(14);
  doc.setTextColor(13, 148, 136);
  doc.text('Summary', 20, yPos + 10);
  
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text(`Self Score: ${appraisal?.scores?.employeeSelfTotal?.toFixed(2) || 'N/A'}`, 20, yPos + 22);
  if (!hideManagerSection) {
    doc.text(`Manager Score: ${appraisal?.scores?.managerTotal?.toFixed(2) || 'N/A'}`, 20, yPos + 30);
  }
  doc.text(`Final Weighted Score: ${appraisal?.scores?.finalWeightedScore?.toFixed(2) || 'N/A'}`, 20, yPos + (hideManagerSection ? 30 : 38));
  
  // Comments
  if (appraisal?.comments?.employee || appraisal?.comments?.manager || appraisal?.comments?.ceo) {
    yPos += 50;
    doc.setFontSize(14);
    doc.setTextColor(13, 148, 136);
    doc.text('Comments', 20, yPos);
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    
    if (appraisal?.comments?.employee) {
      yPos += 12;
      doc.text('Employee:', 20, yPos);
      const empCommentLines = doc.splitTextToSize(appraisal.comments.employee, pageWidth - 50);
      doc.text(empCommentLines, 20, yPos + 6);
      yPos += 6 * empCommentLines.length;
    }
    
    if (!hideManagerSection && appraisal?.comments?.manager) {
      yPos += 12;
      doc.text('Manager:', 20, yPos);
      const mgrCommentLines = doc.splitTextToSize(appraisal.comments.manager, pageWidth - 50);
      doc.text(mgrCommentLines, 20, yPos + 6);
      yPos += 6 * mgrCommentLines.length;
    }
    
    if (appraisal?.comments?.ceo) {
      if (yPos > 260) {
        doc.addPage();
        yPos = 20;
      }
      yPos += 12;
      doc.setFontSize(11);
      doc.setTextColor(13, 148, 136);
      doc.text('CEO Review:', 20, yPos);
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      const ceoCommentLines = doc.splitTextToSize(appraisal.comments.ceo, pageWidth - 50);
      doc.text(ceoCommentLines, 20, yPos + 6);
      yPos += 6 * ceoCommentLines.length;
    }
  }
  
  // Footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated by PerformanceHub - Page ${i} of ${pageCount}`, pageWidth / 2, 290, { align: 'center' });
  }
  
  // Save
  const fileName = `${user?.profile?.firstName || 'Employee'}_${user?.profile?.lastName || ''}_Appraisal_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
  return fileName;
}

// Generate PDF for a list of appraisals (summary view)
export function generateAppraisalListPDF(appraisals, users) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(18);
  doc.setTextColor(13, 148, 136);
  doc.text('Appraisal Report Summary', pageWidth / 2, 16, { align: 'center' });

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 26);
  doc.text(`Total Records: ${appraisals.length}`, 20, 32);

  const headers = ['Employee', 'Dept', 'Title', 'Status', 'Self', 'Final', 'By', 'Due'];
  const colWidths = [32, 20, 32, 22, 14, 14, 20, 22];
  const startX = 14;
  let y = 42;

  const drawRow = (cells) => {
    let x = startX;
    cells.forEach((cell, idx) => {
      doc.text(String(cell || '-'), x, y, { maxWidth: colWidths[idx] });
      x += colWidths[idx];
    });
    y += 7;
  };

  // Helper to determine who finalized
  const getFinalizedBy = (appraisal) => {
    if (appraisal.status !== 'finalized') return '-';
    if (appraisal.ceoApprovedAt) return 'CEO';
    if (appraisal.directFinalize || appraisal.visibleToSupervisor === false) return 'Direct';
    if (appraisal.managerSubmittedAt) return 'Supervisor';
    return 'System';
  };

  // Header row
  doc.setFontSize(9);
  doc.setTextColor(55, 65, 81);
  drawRow(headers);
  doc.line(startX, y - 4, pageWidth - startX, y - 4);

  doc.setTextColor(0, 0, 0);;

  appraisals.forEach((appraisal, idx) => {
    if (y > 280) {
      doc.addPage();
      y = 20;
      drawRow(headers);
      doc.line(startX, y - 4, pageWidth - startX, y - 4);
      doc.setTextColor(0, 0, 0);
    }

    const user = users.find(u => u.id === appraisal.userId) || {};
    const name = `${user?.profile?.firstName || ''} ${user?.profile?.lastName || ''}`.trim() || 'Unknown';
    const dept = user?.profile?.department || '';
    const status = appraisal.status || '';
    const selfScore = appraisal.scores?.employeeSelfTotal != null ? appraisal.scores.employeeSelfTotal.toFixed(1) : '-';
    const finalScore = appraisal.scores?.finalWeightedScore != null ? appraisal.scores.finalWeightedScore.toFixed(1) : '-';
    const finalizedBy = getFinalizedBy(appraisal);
    const due = appraisal.dueDate ? new Date(appraisal.dueDate).toLocaleDateString() : '-';

    drawRow([
      name,
      dept,
      appraisal.title || '',
      status,
      selfScore,
      finalScore,
      finalizedBy,
      due
    ]);
  });

  const fileName = `Appraisal_Report_Summary_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
  return fileName;
}

// Dashboard summary PDF
export function generateDashboardReportPDF(summary, appraisals, users) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(18);
  doc.setTextColor(13, 148, 136);
  doc.text('Admin Dashboard Report', pageWidth / 2, 16, { align: 'center' });

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 26);

  // Summary tiles
  const tiles = [
    { label: 'Employees', value: summary.employees },
    { label: 'Supervisors', value: summary.supervisors },
    { label: 'Pending Reviews', value: summary.pending },
    { label: 'New Hires (30d)', value: summary.newHires },
    { label: 'Active Invites', value: summary.activeInvites }
  ];
  let y = 38;
  doc.setFontSize(11);
  tiles.forEach(tile => {
    doc.text(`${tile.label}: ${tile.value}`, 20, y);
    y += 7;
  });

  // Status breakdown
  y += 2;
  doc.setFontSize(12);
  doc.setTextColor(13, 148, 136);
  doc.text('Status Breakdown', 20, y);
  y += 6;
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  summary.statusData.forEach(s => {
    doc.text(`${s.name}: ${s.value}`, 24, y);
    y += 6;
  });

  // Department overview
  y += 4;
  doc.setFontSize(12);
  doc.setTextColor(13, 148, 136);
  doc.text('Department Overview', 20, y);
  y += 6;
  doc.setFontSize(9);
  doc.setTextColor(55, 65, 81);
  doc.text('Dept', 20, y);
  doc.text('Employees', 60, y);
  doc.text('Appraisals', 100, y);
  y += 5;
  doc.setTextColor(0, 0, 0);
  summary.departmentData.forEach(d => {
    doc.text(String(d.name || '-'), 20, y);
    doc.text(String(d.employees || 0), 60, y);
    doc.text(String(d.appraisals || 0), 100, y);
    y += 6;
    if (y > 270) {
      doc.addPage();
      y = 20;
    }
  });

  // Appraisal list (condensed)
  if (y > 240) {
    doc.addPage();
    y = 20;
  }
  doc.setFontSize(12);
  doc.setTextColor(13, 148, 136);
  doc.text('Appraisals (summary)', 20, y);
  y += 6;
  const headers = ['Employee', 'Status', 'Self', 'Final', 'Due'];
  const colWidths = [45, 25, 15, 15, 25];
  const startX = 14;
  const drawRow = (cells) => {
    let x = startX;
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    cells.forEach((cell, idx) => {
      doc.text(String(cell || '-'), x, y, { maxWidth: colWidths[idx] });
      x += colWidths[idx];
    });
    y += 7;
  };
  drawRow(headers);
  doc.setDrawColor(229, 231, 235);
  doc.line(startX, y - 4, pageWidth - startX, y - 4);
  appraisals.forEach(item => {
    if (y > 280) {
      doc.addPage();
      y = 20;
      drawRow(headers);
      doc.line(startX, y - 4, pageWidth - startX, y - 4);
    }
    const user = users.find(u => u.id === item.userId);
    const name = `${user?.profile?.firstName || ''} ${user?.profile?.lastName || ''}`.trim() || 'Unknown';
    drawRow([
      name,
      item.status || '',
      item.scores?.employeeSelfTotal != null ? item.scores.employeeSelfTotal.toFixed(1) : '-',
      item.scores?.finalWeightedScore != null ? item.scores.finalWeightedScore.toFixed(1) : '-',
      item.dueDate ? new Date(item.dueDate).toLocaleDateString() : '-'
    ]);
  });

  const fileName = `Admin_Dashboard_Report_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
  return fileName;
}

// Generate Excel Report
export function generateAppraisalExcel(appraisals, users) {
  // Create workbook
  const wb = XLSX.utils.book_new();
  
  // Appraisals Summary Sheet
  const summaryData = appraisals.map(a => {
    const user = users.find(u => u.id === a.userId);
    // Determine who finalized
    let finalizedBy = 'N/A';
    if (a.status === 'finalized') {
      if (a.ceoApprovedAt) {
        finalizedBy = 'CEO';
      } else if (a.directFinalize || a.visibleToSupervisor === false) {
        finalizedBy = 'Direct (Auto)';
      } else if (a.managerSubmittedAt) {
        finalizedBy = 'Supervisor';
      } else {
        finalizedBy = 'System';
      }
    }
    return {
      'Employee Name': `${user?.profile?.firstName || ''} ${user?.profile?.lastName || ''}`,
      'Department': user?.profile?.department || 'N/A',
      'Position': user?.profile?.position || 'N/A',
      'Appraisal Title': a.title,
      'Type': a.type,
      'Status': a.status,
      'Self Score': a.scores?.employeeSelfTotal?.toFixed(2) || 'N/A',
      'Manager Score': a.scores?.managerTotal?.toFixed(2) || 'N/A',
      'Final Score': a.scores?.finalWeightedScore?.toFixed(2) || 'N/A',
      'Finalized By': finalizedBy,
      'Employee Comment': a.comments?.employee || '',
      'Manager Comment': a.comments?.manager || '',
      'CEO Review': a.comments?.ceo || '',
      'Due Date': new Date(a.dueDate).toLocaleDateString(),
      'Employee Submitted': a.employeeSubmittedAt ? new Date(a.employeeSubmittedAt).toLocaleString() : 'N/A',
      'Manager Submitted': a.managerSubmittedAt ? new Date(a.managerSubmittedAt).toLocaleString() : (a.finalizedAt ? new Date(a.finalizedAt).toLocaleString() : 'N/A'),
      'CEO Approved': a.ceoApprovedAt ? new Date(a.ceoApprovedAt).toLocaleString() : 'N/A',
      'Finalized Date': a.finalizedAt ? new Date(a.finalizedAt).toLocaleDateString() : 'N/A'
    };
  });
  
  const summarySheet = XLSX.utils.json_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
  
  // Performance Areas Detail Sheet
  const detailData = [];
  appraisals.forEach(a => {
    const user = users.find(u => u.id === a.userId);
    a.performanceAreas?.forEach(area => {
      detailData.push({
        'Employee': `${user?.profile?.firstName || ''} ${user?.profile?.lastName || ''}`,
        'Appraisal': a.title,
        'Competency': area.competencyName,
        'Weight': `${area.weight}%`,
        'Self Score': area.employeeScore ?? 'N/A',
        'Manager Score': area.managerScore ?? 'N/A',
        'Employee Comment': area.employeeComment || '',
        'Manager Comment': area.managerComment || ''
      });
    });
  });
  
  if (detailData.length > 0) {
    const detailSheet = XLSX.utils.json_to_sheet(detailData);
    XLSX.utils.book_append_sheet(wb, detailSheet, 'Performance Details');
  }
  
  // Goals Sheet
  const goalsData = [];
  appraisals.forEach(a => {
    const user = users.find(u => u.id === a.userId);
    [...(a.goals?.current || []), ...(a.goals?.future || [])].forEach(goal => {
      goalsData.push({
        'Employee': `${user?.profile?.firstName || ''} ${user?.profile?.lastName || ''}`,
        'Goal Title': goal.title,
        'Description': goal.description,
        'Status': goal.status,
        'Priority': goal.priority,
        'Due Date': goal.dueDate ? new Date(goal.dueDate).toLocaleDateString() : 'N/A'
      });
    });
  });
  
  if (goalsData.length > 0) {
    const goalsSheet = XLSX.utils.json_to_sheet(goalsData);
    XLSX.utils.book_append_sheet(wb, goalsSheet, 'Goals');
  }
  
  // Save
  const fileName = `Performance_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(wb, fileName);
  return fileName;
}

// Dashboard summary Excel
export function generateDashboardReportExcel(summary, appraisals, users) {
  const wb = XLSX.utils.book_new();

  const summarySheetData = [
    { Metric: 'Employees', Value: summary.employees },
    { Metric: 'Supervisors', Value: summary.supervisors },
    { Metric: 'Pending Reviews', Value: summary.pending },
    { Metric: 'New Hires (30d)', Value: summary.newHires },
    { Metric: 'Active Invites', Value: summary.activeInvites }
  ];
  const summarySheet = XLSX.utils.json_to_sheet(summarySheetData);
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

  const statusSheet = XLSX.utils.json_to_sheet(summary.statusData.map(s => ({ Status: s.name, Count: s.value })));
  XLSX.utils.book_append_sheet(wb, statusSheet, 'Status');

  const deptSheet = XLSX.utils.json_to_sheet(summary.departmentData.map(d => ({ Department: d.name, Employees: d.employees, Appraisals: d.appraisals })));
  XLSX.utils.book_append_sheet(wb, deptSheet, 'Departments');

  const appraisalsSheet = XLSX.utils.json_to_sheet(appraisals.map(a => {
    const user = users.find(u => u.id === a.userId);
    return {
      Employee: `${user?.profile?.firstName || ''} ${user?.profile?.lastName || ''}`.trim() || 'Unknown',
      Department: user?.profile?.department || 'N/A',
      Title: a.title,
      Status: a.status,
      'Self Score': a.scores?.employeeSelfTotal ?? 'N/A',
      'Final Score': a.scores?.finalWeightedScore ?? 'N/A',
      'Due Date': a.dueDate ? new Date(a.dueDate).toLocaleDateString() : 'N/A',
      'Employee Submitted': a.employeeSubmittedAt ? new Date(a.employeeSubmittedAt).toLocaleString() : 'N/A',
      'Manager Submitted': a.managerSubmittedAt || a.finalizedAt ? new Date(a.managerSubmittedAt || a.finalizedAt).toLocaleString() : 'N/A'
    };
  }));
  XLSX.utils.book_append_sheet(wb, appraisalsSheet, 'Appraisals');

  const fileName = `Admin_Dashboard_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(wb, fileName);
  return fileName;
}

// Generate single appraisal Excel
export function generateSingleAppraisalExcel(appraisal, user) {
  const wb = XLSX.utils.book_new();
  
  // Info Sheet
  const infoData = [{
    'Field': 'Employee Name',
    'Value': `${user?.profile?.firstName || ''} ${user?.profile?.lastName || ''}`
  }, {
    'Field': 'Department',
    'Value': user?.profile?.department || 'N/A'
  }, {
    'Field': 'Position',
    'Value': user?.profile?.position || 'N/A'
  }, {
    'Field': 'Appraisal',
    'Value': appraisal?.title || 'Performance Review'
  }, {
    'Field': 'Status',
    'Value': appraisal?.status || 'N/A'
  }, {
    'Field': 'Self Score',
    'Value': appraisal?.scores?.employeeSelfTotal?.toFixed(2) || 'N/A'
  }, {
    'Field': 'Manager Score',
    'Value': appraisal?.scores?.managerTotal?.toFixed(2) || 'N/A'
  }, {
    'Field': 'Final Score',
    'Value': appraisal?.scores?.finalWeightedScore?.toFixed(2) || 'N/A'
  }, {
    'Field': 'Employee Submitted',
    'Value': appraisal?.employeeSubmittedAt ? new Date(appraisal.employeeSubmittedAt).toLocaleString() : 'N/A'
  }, {
    'Field': 'Manager Submitted',
    'Value': appraisal?.managerSubmittedAt ? new Date(appraisal.managerSubmittedAt).toLocaleString() : (appraisal?.finalizedAt ? new Date(appraisal.finalizedAt).toLocaleString() : 'N/A')
  }];
  
  const infoSheet = XLSX.utils.json_to_sheet(infoData);
  XLSX.utils.book_append_sheet(wb, infoSheet, 'Info');
  
  // Performance Areas
  const areasData = appraisal?.performanceAreas?.map(area => ({
    'Competency': area.competencyName,
    'Weight': `${area.weight}%`,
    'Self Score': area.employeeScore ?? 'N/A',
    'Manager Score': area.managerScore ?? 'N/A',
    'Employee Comment': area.employeeComment || '',
    'Manager Comment': area.managerComment || ''
  })) || [];
  
  if (areasData.length > 0) {
    const areasSheet = XLSX.utils.json_to_sheet(areasData);
    XLSX.utils.book_append_sheet(wb, areasSheet, 'Performance Areas');
  }
  
  const fileName = `${user?.profile?.firstName || 'Employee'}_Appraisal_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(wb, fileName);
  return fileName;
}

// Export data to CSV
export function exportToCSV(data, filename = 'export') {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Get headers from the first object
  const headers = Object.keys(data[0]);
  
  // Create CSV content
  const csvRows = [];
  
  // Add headers
  csvRows.push(headers.join(','));
  
  // Add data rows
  for (const row of data) {
    const values = headers.map(header => {
      const value = row[header];
      // Handle null/undefined
      if (value === null || value === undefined) return '';
      // Handle strings with commas, quotes, or newlines
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    });
    csvRows.push(values.join(','));
  }
  
  const csvContent = csvRows.join('\n');
  
  // Create blob and download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, `${filename}.csv`);
  } else {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
  
  return `${filename}.csv`;
}

// Generate Payslip PDF
export function generatePayslipPDF(payslip, employee, companyName = 'PerformanceHub Inc.') {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };
  
  const getMonthName = (month) => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 
                    'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month - 1] || 'Unknown';
  };
  
  // Header
  doc.setFillColor(13, 148, 136); // Teal
  doc.rect(0, 0, pageWidth, 30, 'F');
  doc.setFontSize(18);
  doc.setTextColor(255, 255, 255);
  doc.text('PAYSLIP', pageWidth / 2, 15, { align: 'center' });
  doc.setFontSize(10);
  doc.text(companyName, pageWidth / 2, 23, { align: 'center' });
  
  // Pay Period Box
  doc.setFillColor(240, 240, 240);
  doc.rect(15, 35, pageWidth - 30, 20, 'F');
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text(`Pay Period: ${payslip.periodName || `${getMonthName(payslip.month)} ${payslip.year}`}`, 20, 45);
  doc.text(`Pay Frequency: ${payslip.payFrequency || 'Bi-Weekly'}`, 120, 45);
  doc.text(`Pay Date: ${payslip.generatedAt ? new Date(payslip.generatedAt).toLocaleDateString() : new Date().toLocaleDateString()}`, 20, 52);
  
  // Employee Details
  doc.setFontSize(12);
  doc.setTextColor(13, 148, 136);
  doc.text('EMPLOYEE DETAILS', 15, 68);
  doc.setDrawColor(13, 148, 136);
  doc.line(15, 70, pageWidth - 15, 70);
  
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(`Name: ${employee?.profile?.firstName || ''} ${employee?.profile?.lastName || ''}`, 15, 78);
  doc.text(`Employee ID: ${payslip.userId?.substring(0, 8) || 'N/A'}`, 15, 85);
  doc.text(`Department: ${employee?.profile?.department || 'N/A'}`, 110, 78);
  doc.text(`Position: ${employee?.profile?.position || 'N/A'}`, 110, 85);
  doc.text(`Work State: ${payslip.workState || employee?.profile?.workState || 'N/A'}`, 15, 92);
  doc.text(`Filing Status: ${payslip.filingStatus || 'N/A'}`, 110, 92);
  
  // Earnings Section
  let yPos = 105;
  doc.setFontSize(12);
  doc.setTextColor(13, 148, 136);
  doc.text('EARNINGS', 15, yPos);
  doc.line(15, yPos + 2, 100, yPos + 2);
  
  const earnings = payslip.earnings || {};
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  yPos += 10;
  
  const earningsItems = [
    ['Regular Pay', earnings.regular || payslip.grossPay || 0],
    ['Overtime', earnings.overtime || 0],
    ['Bonus', earnings.bonus || 0],
    ['Commission', earnings.commission || 0],
    ['Tips', earnings.tips || 0]
  ].filter(([_, val]) => val > 0);
  
  earningsItems.forEach(([label, value]) => {
    doc.text(label, 20, yPos);
    doc.text(formatCurrency(value), 90, yPos, { align: 'right' });
    yPos += 7;
  });
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('Gross Pay:', 20, yPos + 3);
  doc.text(formatCurrency(payslip.grossPay || 0), 90, yPos + 3, { align: 'right' });
  doc.setFont(undefined, 'normal');
  
  // Taxes Section
  yPos = 105;
  doc.setFontSize(12);
  doc.setTextColor(13, 148, 136);
  doc.text('TAXES', 110, yPos);
  doc.line(110, yPos + 2, pageWidth - 15, yPos + 2);
  
  const taxes = payslip.taxes || {};
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  yPos += 10;
  
  const taxItems = [
    ['Federal Tax', taxes.federal || 0],
    ['State Tax', taxes.state || 0],
    ['Social Security', taxes.socialSecurity || 0],
    ['Medicare', taxes.medicare || 0],
    ['Local Tax', taxes.local || 0],
    ['Additional Medicare', taxes.additionalMedicare || 0]
  ].filter(([_, val]) => val > 0);
  
  taxItems.forEach(([label, value]) => {
    doc.text(label, 115, yPos);
    doc.text(formatCurrency(value), pageWidth - 20, yPos, { align: 'right' });
    yPos += 7;
  });
  
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('Total Taxes:', 115, yPos + 3);
  doc.text(formatCurrency(taxes.total || Object.values(taxes).reduce((sum, v) => sum + (Number(v) || 0), 0)), pageWidth - 20, yPos + 3, { align: 'right' });
  doc.setFont(undefined, 'normal');
  
  // Pre-Tax Deductions
  yPos = 165;
  doc.setFontSize(12);
  doc.setTextColor(13, 148, 136);
  doc.text('PRE-TAX DEDUCTIONS', 15, yPos);
  doc.line(15, yPos + 2, 100, yPos + 2);
  
  const preTaxDeductions = payslip.preTaxDeductions || {};
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  yPos += 10;
  
  const preTaxItems = [
    ['401(k)', preTaxDeductions.retirement401k || 0],
    ['Health Insurance', preTaxDeductions.healthInsurance || 0],
    ['HSA', preTaxDeductions.hsa || 0],
    ['FSA', preTaxDeductions.fsa || 0],
    ['Dental Insurance', preTaxDeductions.dentalInsurance || 0],
    ['Vision Insurance', preTaxDeductions.visionInsurance || 0]
  ].filter(([_, val]) => val > 0);
  
  preTaxItems.forEach(([label, value]) => {
    doc.text(label, 20, yPos);
    doc.text(formatCurrency(value), 90, yPos, { align: 'right' });
    yPos += 7;
  });
  
  const totalPreTax = preTaxItems.reduce((sum, [_, val]) => sum + Number(val), 0);
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('Total Pre-Tax:', 20, yPos + 3);
  doc.text(formatCurrency(totalPreTax), 90, yPos + 3, { align: 'right' });
  doc.setFont(undefined, 'normal');
  
  // Post-Tax Deductions
  yPos = 165;
  doc.setFontSize(12);
  doc.setTextColor(13, 148, 136);
  doc.text('POST-TAX DEDUCTIONS', 110, yPos);
  doc.line(110, yPos + 2, pageWidth - 15, yPos + 2);
  
  const postTaxDeductions = payslip.postTaxDeductions || {};
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  yPos += 10;
  
  const postTaxItems = [
    ['Roth 401(k)', postTaxDeductions.roth401k || 0],
    ['Life Insurance', postTaxDeductions.lifeInsurance || 0],
    ['Garnishment', postTaxDeductions.garnishment || 0],
    ['Other', postTaxDeductions.other || 0]
  ].filter(([_, val]) => val > 0);
  
  if (postTaxItems.length > 0) {
    postTaxItems.forEach(([label, value]) => {
      doc.text(label, 115, yPos);
      doc.text(formatCurrency(value), pageWidth - 20, yPos, { align: 'right' });
      yPos += 7;
    });
    const totalPostTax = postTaxItems.reduce((sum, [_, val]) => sum + Number(val), 0);
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.text('Total Post-Tax:', 115, yPos + 3);
    doc.text(formatCurrency(totalPostTax), pageWidth - 20, yPos + 3, { align: 'right' });
    doc.setFont(undefined, 'normal');
  } else {
    doc.text('None', 115, yPos);
  }
  
  // Net Pay Box
  yPos = 225;
  doc.setFillColor(13, 148, 136);
  doc.rect(15, yPos, pageWidth - 30, 18, 'F');
  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.text('NET PAY:', 25, yPos + 12);
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text(formatCurrency(payslip.netPay || 0), pageWidth - 25, yPos + 12, { align: 'right' });
  doc.setFont(undefined, 'normal');
  
  // YTD Summary
  if (payslip.ytd) {
    yPos = 255;
    doc.setFontSize(12);
    doc.setTextColor(13, 148, 136);
    doc.text('YEAR-TO-DATE SUMMARY', 15, yPos);
    doc.line(15, yPos + 2, pageWidth - 15, yPos + 2);
    
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    yPos += 10;
    
    const ytd = payslip.ytd;
    const ytdItems = [
      ['YTD Gross', ytd.gross || 0],
      ['YTD Federal Tax', ytd.federalTax || 0],
      ['YTD State Tax', ytd.stateTax || 0],
      ['YTD Social Security', ytd.socialSecurity || 0],
      ['YTD Medicare', ytd.medicare || 0],
      ['YTD Net Pay', ytd.netPay || 0]
    ];
    
    ytdItems.forEach(([label, value], idx) => {
      const xPos = idx < 3 ? 20 : 110;
      const yOffset = (idx % 3) * 7;
      doc.text(label, xPos, yPos + yOffset);
      doc.text(formatCurrency(value), xPos + 75, yPos + yOffset, { align: 'right' });
    });
  }
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text('This is a computer-generated payslip and does not require a signature.', pageWidth / 2, 285, { align: 'center' });
  doc.text(`Generated on ${new Date().toLocaleString()}`, pageWidth / 2, 290, { align: 'center' });
  
  // Download
  const filename = `Payslip_${employee?.profile?.firstName || 'Employee'}_${payslip.periodName || `${getMonthName(payslip.month)}_${payslip.year}`}.pdf`;
  doc.save(filename);
  
  return filename;
}

// Generate W-2 PDF
export function generateW2PDF(w2Data, year) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };
  
  // Header
  doc.setFillColor(20, 80, 140);
  doc.rect(0, 0, pageWidth, 25, 'F');
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text(`Form W-2 - Wage and Tax Statement`, pageWidth / 2, 12, { align: 'center' });
  doc.setFontSize(12);
  doc.text(`Tax Year ${year}`, pageWidth / 2, 20, { align: 'center' });
  
  // Employee SSN and Employer EIN
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  
  // Box layout
  const boxY = 32;
  const boxHeight = 20;
  const col1 = 15;
  const col2 = 110;
  
  // Row 1: SSN and EIN
  doc.setDrawColor(150);
  doc.rect(col1, boxY, 90, boxHeight);
  doc.rect(col2, boxY, 85, boxHeight);
  doc.setFontSize(7);
  doc.setTextColor(100);
  doc.text('a Employee\'s social security number', col1 + 2, boxY + 5);
  doc.text('b Employer identification number (EIN)', col2 + 2, boxY + 5);
  doc.setFontSize(10);
  doc.setTextColor(0);
  doc.text(w2Data.ssn || 'XXX-XX-XXXX', col1 + 2, boxY + 14);
  doc.text(w2Data.ein || 'XX-XXXXXXX', col2 + 2, boxY + 14);
  
  // Row 2: Employer Name/Address and Wages
  const row2Y = boxY + boxHeight;
  doc.rect(col1, row2Y, 90, 35);
  doc.rect(col2, row2Y, 40, boxHeight);
  doc.rect(col2 + 40, row2Y, 45, boxHeight);
  doc.setFontSize(7);
  doc.setTextColor(100);
  doc.text('c Employer\'s name, address, and ZIP', col1 + 2, row2Y + 5);
  doc.text('1 Wages, tips, compensation', col2 + 2, row2Y + 5);
  doc.text('2 Federal income tax withheld', col2 + 42, row2Y + 5);
  doc.setFontSize(9);
  doc.setTextColor(0);
  doc.text(w2Data.employerName || 'Company Name', col1 + 2, row2Y + 14);
  doc.text(w2Data.employerAddress || '', col1 + 2, row2Y + 22);
  doc.setFontSize(10);
  doc.text(formatCurrency(w2Data.wages), col2 + 2, row2Y + 14);
  doc.text(formatCurrency(w2Data.federalTax), col2 + 42, row2Y + 14);
  
  // Row 3: More boxes
  const row3Y = row2Y + boxHeight;
  doc.rect(col2, row3Y, 40, boxHeight);
  doc.rect(col2 + 40, row3Y, 45, boxHeight);
  doc.setFontSize(7);
  doc.setTextColor(100);
  doc.text('3 Social security wages', col2 + 2, row3Y + 5);
  doc.text('4 Social security tax withheld', col2 + 42, row3Y + 5);
  doc.setFontSize(10);
  doc.setTextColor(0);
  doc.text(formatCurrency(w2Data.socialSecurityWages), col2 + 2, row3Y + 14);
  doc.text(formatCurrency(w2Data.socialSecurityTax), col2 + 42, row3Y + 14);
  
  // Row 4
  const row4Y = row3Y + boxHeight;
  doc.rect(col1, row4Y, 90, 30);
  doc.rect(col2, row4Y, 40, boxHeight);
  doc.rect(col2 + 40, row4Y, 45, boxHeight);
  doc.setFontSize(7);
  doc.setTextColor(100);
  doc.text('e Employee\'s name, address, and ZIP', col1 + 2, row4Y + 5);
  doc.text('5 Medicare wages and tips', col2 + 2, row4Y + 5);
  doc.text('6 Medicare tax withheld', col2 + 42, row4Y + 5);
  doc.setFontSize(10);
  doc.setTextColor(0);
  doc.text(w2Data.employeeName || 'Employee Name', col1 + 2, row4Y + 14);
  doc.text(w2Data.employeeAddress || '', col1 + 2, row4Y + 22);
  doc.text(formatCurrency(w2Data.medicareWages), col2 + 2, row4Y + 14);
  doc.text(formatCurrency(w2Data.medicareTax), col2 + 42, row4Y + 14);
  
  // Row 5
  const row5Y = row4Y + boxHeight;
  doc.rect(col2, row5Y, 40, boxHeight);
  doc.rect(col2 + 40, row5Y, 45, boxHeight);
  doc.setFontSize(7);
  doc.setTextColor(100);
  doc.text('7 Social security tips', col2 + 2, row5Y + 5);
  doc.text('8 Allocated tips', col2 + 42, row5Y + 5);
  doc.setFontSize(10);
  doc.setTextColor(0);
  doc.text(formatCurrency(w2Data.socialSecurityTips || 0), col2 + 2, row5Y + 14);
  doc.text(formatCurrency(w2Data.allocatedTips || 0), col2 + 42, row5Y + 14);
  
  // State/Local section
  const stateY = row5Y + boxHeight + 10;
  doc.rect(col1, stateY, 30, boxHeight);
  doc.rect(col1 + 30, stateY, 30, boxHeight);
  doc.rect(col1 + 60, stateY, 45, boxHeight);
  doc.rect(col1 + 105, stateY, 45, boxHeight);
  doc.rect(col1 + 150, stateY, 45, boxHeight);
  doc.setFontSize(7);
  doc.setTextColor(100);
  doc.text('15 State', col1 + 2, stateY + 5);
  doc.text('Employer\'s state ID', col1 + 32, stateY + 5);
  doc.text('16 State wages, tips, etc.', col1 + 62, stateY + 5);
  doc.text('17 State income tax', col1 + 107, stateY + 5);
  doc.text('18 Local wages, tips, etc.', col1 + 152, stateY + 5);
  doc.setFontSize(9);
  doc.setTextColor(0);
  doc.text(w2Data.stateCode || '', col1 + 2, stateY + 14);
  doc.text(w2Data.stateId || '', col1 + 32, stateY + 14);
  doc.text(formatCurrency(w2Data.stateWages || w2Data.wages), col1 + 62, stateY + 14);
  doc.text(formatCurrency(w2Data.stateTax), col1 + 107, stateY + 14);
  doc.text(formatCurrency(w2Data.localWages || 0), col1 + 152, stateY + 14);
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128);
  doc.text('This is a computer-generated W-2 form for record keeping purposes.', pageWidth / 2, 280, { align: 'center' });
  doc.text(`Generated on ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
  
  const filename = `W2-${year}-${w2Data.employeeName?.replace(/\s+/g, '_') || 'employee'}.pdf`;
  doc.save(filename);
  
  return filename;
}

// Generate W-3 Transmittal PDF
export function generateW3PDF(summary, companyInfo, year) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };
  
  // Header
  doc.setFillColor(20, 80, 140);
  doc.rect(0, 0, pageWidth, 30, 'F');
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text('Form W-3 - Transmittal of Wage and Tax Statements', pageWidth / 2, 14, { align: 'center' });
  doc.setFontSize(12);
  doc.text(`Tax Year ${year}`, pageWidth / 2, 24, { align: 'center' });
  
  // Employer Information
  let yPos = 45;
  doc.setFontSize(12);
  doc.setTextColor(20, 80, 140);
  doc.text('EMPLOYER INFORMATION', 15, yPos);
  doc.setDrawColor(20, 80, 140);
  doc.line(15, yPos + 2, 100, yPos + 2);
  
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  yPos += 12;
  doc.text(`Employer Name: ${companyInfo?.legalName || 'PerformanceHub Inc.'}`, 15, yPos);
  yPos += 7;
  doc.text(`EIN: ${companyInfo?.ein || 'XX-XXXXXXX'}`, 15, yPos);
  yPos += 7;
  doc.text(`Address: ${companyInfo?.address || 'N/A'}`, 15, yPos);
  yPos += 7;
  doc.text(`Contact: ${companyInfo?.contact || 'N/A'}`, 15, yPos);
  
  // Summary Statistics
  yPos += 20;
  doc.setFillColor(240, 240, 240);
  doc.rect(15, yPos, pageWidth - 30, 25, 'F');
  doc.setFontSize(11);
  doc.setTextColor(20, 80, 140);
  doc.text('TRANSMITTAL SUMMARY', 20, yPos + 8);
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(`Number of W-2 Forms: ${summary.formsGenerated || 0}`, 20, yPos + 18);
  doc.text(`Total Employees: ${summary.formsGenerated || 0}`, 120, yPos + 18);
  
  // Box totals
  yPos += 35;
  doc.setFontSize(12);
  doc.setTextColor(20, 80, 140);
  doc.text('WAGE AND TAX TOTALS', 15, yPos);
  doc.line(15, yPos + 2, pageWidth - 15, yPos + 2);
  
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  yPos += 12;
  
  const boxes = [
    ['Box 1 - Wages, tips, compensation', summary.totalWages || 0],
    ['Box 2 - Federal income tax withheld', summary.totalFederalWithheld || 0],
    ['Box 3 - Social security wages', summary.totalSocialSecurityWages || 0],
    ['Box 4 - Social security tax withheld', summary.totalSocialSecurity || 0],
    ['Box 5 - Medicare wages and tips', summary.totalMedicareWages || 0],
    ['Box 6 - Medicare tax withheld', summary.totalMedicare || 0],
    ['Box 16 - State wages, tips, etc.', summary.totalStateWages || 0],
    ['Box 17 - State income tax', summary.totalStateTax || 0]
  ];
  
  boxes.forEach(([label, value]) => {
    doc.setDrawColor(200);
    doc.rect(15, yPos - 5, pageWidth - 30, 12);
    doc.text(label, 20, yPos + 2);
    doc.text(formatCurrency(value), pageWidth - 25, yPos + 2, { align: 'right' });
    yPos += 12;
  });
  
  // Grand Total
  yPos += 5;
  doc.setFillColor(20, 80, 140);
  doc.rect(15, yPos, pageWidth - 30, 15, 'F');
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.text('Total Wages (Box 1)', 20, yPos + 10);
  doc.setFont(undefined, 'bold');
  doc.text(formatCurrency(summary.totalWages || 0), pageWidth - 25, yPos + 10, { align: 'right' });
  doc.setFont(undefined, 'normal');
  
  // Filing Information
  yPos += 30;
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text('FILING INFORMATION', 15, yPos);
  yPos += 8;
  doc.setFontSize(9);
  doc.text('• File this transmittal with Copy A of all W-2 forms', 15, yPos);
  yPos += 6;
  doc.text('• Submit to Social Security Administration by January 31', 15, yPos);
  yPos += 6;
  doc.text('• Electronic filing required for 250+ W-2s', 15, yPos);
  yPos += 6;
  doc.text('• Use BSO at ssa.gov/bso for electronic filing', 15, yPos);
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128);
  doc.text('This is a computer-generated W-3 transmittal for record keeping purposes.', pageWidth / 2, 280, { align: 'center' });
  doc.text(`Generated on ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
  
  const filename = `W3-Transmittal-${year}.pdf`;
  doc.save(filename);
  
  return filename;
}

// Generate W-2c Correction PDF
export function generateW2cPDF(originalW2, correctedW2, year, correctionReason) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };
  
  // Header
  doc.setFillColor(180, 60, 60);
  doc.rect(0, 0, pageWidth, 25, 'F');
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text('Form W-2c - Corrected Wage and Tax Statement', pageWidth / 2, 12, { align: 'center' });
  doc.setFontSize(12);
  doc.text(`Tax Year ${year}`, pageWidth / 2, 20, { align: 'center' });
  
  // Employee Info
  let yPos = 35;
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(`Employee Name: ${correctedW2.employeeName || 'N/A'}`, 15, yPos);
  doc.text(`SSN: ${correctedW2.ssn || 'XXX-XX-XXXX'}`, 120, yPos);
  yPos += 8;
  doc.text(`Employer: ${correctedW2.employerName || 'N/A'}`, 15, yPos);
  doc.text(`EIN: ${correctedW2.ein || 'XX-XXXXXXX'}`, 120, yPos);
  
  // Correction Reason
  yPos += 15;
  doc.setFillColor(255, 240, 240);
  doc.rect(15, yPos, pageWidth - 30, 15, 'F');
  doc.setFontSize(9);
  doc.setTextColor(180, 60, 60);
  doc.text('Reason for Correction:', 20, yPos + 6);
  doc.setTextColor(0, 0, 0);
  doc.text(correctionReason || 'Wage/tax amount correction', 20, yPos + 12);
  
  // Comparison Table
  yPos += 25;
  doc.setFontSize(11);
  doc.setTextColor(20, 80, 140);
  doc.text('CORRECTIONS', 15, yPos);
  doc.line(15, yPos + 2, pageWidth - 15, yPos + 2);
  
  yPos += 10;
  
  // Table headers
  doc.setFillColor(240, 240, 240);
  doc.rect(15, yPos, pageWidth - 30, 10, 'F');
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text('Box', 20, yPos + 7);
  doc.text('Previously Reported', 70, yPos + 7);
  doc.text('Correct Amount', 130, yPos + 7);
  
  yPos += 15;
  doc.setTextColor(0, 0, 0);
  
  const corrections = [
    ['1 - Wages', originalW2.wages || 0, correctedW2.wages || 0],
    ['2 - Federal Tax', originalW2.federalTax || 0, correctedW2.federalTax || 0],
    ['3 - SS Wages', originalW2.socialSecurityWages || 0, correctedW2.socialSecurityWages || 0],
    ['4 - SS Tax', originalW2.socialSecurityTax || 0, correctedW2.socialSecurityTax || 0],
    ['5 - Medicare Wages', originalW2.medicareWages || 0, correctedW2.medicareWages || 0],
    ['6 - Medicare Tax', originalW2.medicareTax || 0, correctedW2.medicareTax || 0],
    ['16 - State Wages', originalW2.stateWages || 0, correctedW2.stateWages || 0],
    ['17 - State Tax', originalW2.stateTax || 0, correctedW2.stateTax || 0]
  ];
  
  corrections.forEach(([label, original, corrected]) => {
    const hasChange = original !== corrected;
    if (hasChange) {
      doc.setFillColor(255, 255, 200);
      doc.rect(15, yPos - 4, pageWidth - 30, 10, 'F');
    }
    doc.setDrawColor(220);
    doc.rect(15, yPos - 4, pageWidth - 30, 10);
    doc.text(label, 20, yPos + 2);
    doc.text(formatCurrency(original), 70, yPos + 2);
    doc.setTextColor(hasChange ? 180 : 0, hasChange ? 60 : 0, hasChange ? 60 : 0);
    doc.text(formatCurrency(corrected), 130, yPos + 2);
    doc.setTextColor(0, 0, 0);
    yPos += 10;
  });
  
  // Instructions
  yPos += 15;
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text('Instructions:', 15, yPos);
  yPos += 6;
  doc.text('• Provide corrected W-2c to employee', 15, yPos);
  yPos += 5;
  doc.text('• File Copy A with SSA along with Form W-3c', 15, yPos);
  yPos += 5;
  doc.text('• Keep Copy D for employer records', 15, yPos);
  
  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128);
  doc.text('This is a computer-generated W-2c correction form.', pageWidth / 2, 280, { align: 'center' });
  doc.text(`Generated on ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
  
  const filename = `W2c-${year}-${correctedW2.employeeName?.replace(/\s+/g, '_') || 'employee'}.pdf`;
  doc.save(filename);
  
  return filename;
}

// ============================================================================
// W-4 Form PDF Generation (Employee's Withholding Certificate)
// ============================================================================

/**
 * Generate a filled W-4 PDF based on employee data
 * @param {Object} w4Data - W-4 form data
 * @param {Object} employee - Employee information
 * @param {Object} company - Company information  
 * @returns {string} - Filename of generated PDF
 */
export function generateW4PDF(w4Data, employee, company = {}) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0;
    return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };
  
  // Colors
  const tealColor = [13, 148, 136];
  const darkGray = [50, 50, 50];
  const lightGray = [150, 150, 150];
  
  // ========== HEADER ==========
  doc.setFillColor(13, 148, 136);
  doc.rect(0, 0, pageWidth, 35, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('Form W-4', 15, 18);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text("Employee's Withholding Certificate", 15, 26);
  
  doc.setFontSize(9);
  doc.text('Department of the Treasury - Internal Revenue Service', pageWidth - 15, 18, { align: 'right' });
  doc.text(`Tax Year ${new Date().getFullYear()}`, pageWidth - 15, 26, { align: 'right' });
  
  // ========== EMPLOYEE INFO SECTION ==========
  let y = 45;
  
  doc.setTextColor(...darkGray);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Step 1: Personal Information', 15, y);
  y += 8;
  
  // Employee name box
  doc.setDrawColor(200, 200, 200);
  doc.setFillColor(250, 250, 250);
  doc.roundedRect(15, y, pageWidth - 30, 25, 2, 2, 'FD');
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...lightGray);
  doc.text('(a) First name and middle initial', 18, y + 5);
  doc.text('Last name', 90, y + 5);
  
  doc.setTextColor(...darkGray);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  const firstName = employee?.profile?.firstName || '';
  const middleInitial = employee?.profile?.middleName ? employee.profile.middleName.charAt(0) + '.' : '';
  const lastName = employee?.profile?.lastName || '';
  doc.text(`${firstName} ${middleInitial}`, 18, y + 15);
  doc.text(lastName, 90, y + 15);
  
  // SSN
  doc.setTextColor(...lightGray);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('(b) Social security number', 155, y + 5);
  doc.setTextColor(...darkGray);
  doc.setFontSize(10);
  doc.text(employee?.profile?.ssn ? `XXX-XX-${employee.profile.ssn.slice(-4)}` : 'XXX-XX-XXXX', 155, y + 15);
  
  y += 30;
  
  // Address box
  doc.setFillColor(250, 250, 250);
  doc.roundedRect(15, y, pageWidth - 30, 20, 2, 2, 'FD');
  
  doc.setTextColor(...lightGray);
  doc.setFontSize(8);
  doc.text('(c) Address', 18, y + 5);
  
  doc.setTextColor(...darkGray);
  doc.setFontSize(10);
  const address = employee?.profile?.mailingAddress1 || employee?.profile?.address?.street || '';
  const city = employee?.profile?.mailingCity || employee?.profile?.address?.city || '';
  const state = employee?.profile?.mailingState || employee?.profile?.address?.state || '';
  const zip = employee?.profile?.mailingZip || employee?.profile?.address?.zip || '';
  doc.text(`${address}`, 18, y + 12);
  doc.text(`${city}, ${state} ${zip}`, 18, y + 17);
  
  y += 25;
  
  // Filing Status
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkGray);
  doc.text('(d) Filing Status', 15, y);
  y += 6;
  
  // Filing status checkboxes
  const filingStatuses = [
    { value: 'single', label: 'Single or Married filing separately' },
    { value: 'married', label: 'Married filing jointly' },
    { value: 'headOfHousehold', label: 'Head of household' }
  ];
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  let xPos = 15;
  filingStatuses.forEach(status => {
    const isChecked = w4Data?.filingStatus === status.value;
    
    // Checkbox
    doc.setDrawColor(150, 150, 150);
    doc.setFillColor(255, 255, 255);
    doc.rect(xPos, y - 3, 4, 4, 'FD');
    
    if (isChecked) {
      doc.setFillColor(13, 148, 136);
      doc.rect(xPos + 0.5, y - 2.5, 3, 3, 'F');
    }
    
    doc.setTextColor(...darkGray);
    doc.text(status.label, xPos + 6, y);
    xPos += 60;
  });
  
  y += 12;
  
  // ========== STEP 2: MULTIPLE JOBS ==========
  doc.setFillColor(245, 245, 245);
  doc.rect(15, y, pageWidth - 30, 25, 'F');
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...tealColor);
  doc.text('Step 2: Multiple Jobs or Spouse Works', 18, y + 8);
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkGray);
  
  // Checkbox for multiple jobs
  doc.setDrawColor(150, 150, 150);
  doc.setFillColor(255, 255, 255);
  doc.rect(18, y + 14, 4, 4, 'FD');
  
  if (w4Data?.multipleJobs) {
    doc.setFillColor(13, 148, 136);
    doc.rect(18.5, y + 14.5, 3, 3, 'F');
  }
  
  doc.text('Check here if you have more than one job or your spouse also works', 25, y + 17);
  
  y += 32;
  
  // ========== STEP 3: DEPENDENTS ==========
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkGray);
  doc.text('Step 3: Claim Dependents', 15, y);
  y += 8;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  
  const dependentsData = [
    { label: 'Qualifying children under 17 × $2,000', count: w4Data?.qualifyingChildren || 0, multiplier: 2000 },
    { label: 'Other dependents × $500', count: w4Data?.otherDependents || 0, multiplier: 500 }
  ];
  
  dependentsData.forEach(item => {
    doc.setTextColor(...darkGray);
    doc.text(item.label, 18, y);
    
    // Count box
    doc.setDrawColor(200, 200, 200);
    doc.setFillColor(255, 255, 255);
    doc.rect(120, y - 4, 15, 6, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.text(item.count.toString(), 125, y, { align: 'center' });
    
    // Amount
    const amount = item.count * item.multiplier;
    doc.setFillColor(250, 250, 250);
    doc.rect(145, y - 4, 30, 6, 'FD');
    doc.setTextColor(...tealColor);
    doc.text(`$${formatCurrency(amount)}`, 160, y, { align: 'center' });
    
    doc.setFont('helvetica', 'normal');
    y += 8;
  });
  
  // Total dependent credit
  doc.setDrawColor(...tealColor);
  doc.line(145, y - 2, 175, y - 2);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkGray);
  doc.text('Total (Step 3):', 100, y + 4);
  doc.setTextColor(...tealColor);
  doc.text(`$${formatCurrency(w4Data?.dependentCredit || 0)}`, 160, y + 4, { align: 'center' });
  
  y += 15;
  
  // ========== STEP 4: OTHER ADJUSTMENTS ==========
  doc.setFillColor(245, 245, 245);
  doc.rect(15, y, pageWidth - 30, 45, 'F');
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...tealColor);
  doc.text('Step 4: Other Adjustments (Optional)', 18, y + 8);
  y += 15;
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkGray);
  
  const adjustments = [
    { label: '(a) Other income (not from jobs)', value: w4Data?.otherIncome || 0 },
    { label: '(b) Deductions (other than standard)', value: w4Data?.deductions || 0 },
    { label: '(c) Extra withholding per pay period', value: w4Data?.additionalWithholding || w4Data?.extraWithholding || 0 }
  ];
  
  adjustments.forEach(item => {
    doc.text(item.label, 22, y);
    
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(200, 200, 200);
    doc.rect(140, y - 4, 35, 6, 'FD');
    
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...tealColor);
    doc.text(`$${formatCurrency(item.value)}`, 157, y, { align: 'center' });
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkGray);
    y += 9;
  });
  
  y += 10;
  
  // ========== STEP 5: SIGNATURE ==========
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkGray);
  doc.text('Step 5: Sign Here', 15, y);
  y += 10;
  
  // Signature line
  doc.setDrawColor(150, 150, 150);
  doc.line(15, y + 10, 100, y + 10);
  doc.line(110, y + 10, 145, y + 10);
  
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...lightGray);
  doc.text("Employee's signature", 15, y + 15);
  doc.text('Date', 110, y + 15);
  
  // Pre-fill signature date if available
  if (w4Data?.updatedAt || w4Data?.signedDate) {
    doc.setTextColor(...darkGray);
    doc.setFontSize(9);
    doc.text(new Date(w4Data.updatedAt || w4Data.signedDate).toLocaleDateString(), 110, y + 8);
  }
  
  y += 25;
  
  // ========== EMPLOYER SECTION ==========
  doc.setFillColor(13, 148, 136);
  doc.rect(15, y, pageWidth - 30, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text("Employers Only", 18, y + 5.5);
  y += 12;
  
  doc.setDrawColor(200, 200, 200);
  doc.setFillColor(252, 252, 252);
  doc.rect(15, y, pageWidth - 30, 28, 'FD');
  
  doc.setFontSize(8);
  doc.setTextColor(...lightGray);
  doc.text("Employer's name and address", 18, y + 5);
  doc.text('First date of employment', 130, y + 5);
  doc.text('EIN', 130, y + 18);
  
  doc.setTextColor(...darkGray);
  doc.setFontSize(9);
  doc.text(company?.name || 'PerformanceHub Inc.', 18, y + 12);
  doc.text(company?.address || '', 18, y + 18);
  
  if (employee?.profile?.hireDate || employee?.createdAt) {
    doc.text(new Date(employee.profile?.hireDate || employee.createdAt).toLocaleDateString(), 130, y + 12);
  }
  
  if (company?.ein) {
    doc.text(company.ein, 130, y + 24);
  }
  
  // ========== FOOTER ==========
  doc.setFontSize(7);
  doc.setTextColor(128, 128, 128);
  doc.text('Form W-4 (2024) - This is a computer-generated form based on employee-provided information.', pageWidth / 2, 280, { align: 'center' });
  doc.text(`Generated on ${new Date().toLocaleString()}`, pageWidth / 2, 285, { align: 'center' });
  
  const filename = `W4-${employee?.profile?.firstName || 'Employee'}_${employee?.profile?.lastName || ''}_${new Date().getFullYear()}.pdf`;
  doc.save(filename);
  
  return filename;
}